import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import './Messages.css';
import axios from 'axios';
import SendMessageIcon from '../assets/SendMessageIcon';
import ClockIcon from '../assets/ClockIcon';
import EditIcon from '../assets/EditIcon';
import { CircularProgress } from '@mui/material';
import { SaveRounded } from '@mui/icons-material';
import DeleteIcon from '../assets/DeleteIcon';

function Messages({serverURL, setConversationID}) {
  const { conversationID } = useParams(); // Extracts 'id' from the URL
  setConversationID(conversationID);
  const [messages, setMessages] = useState([]);
  const [newMessageText, setNewMessageText] = useState('');
  const [AIMessages, setAIMessages] = useState([]);
  const [timeDifferences, setTimeDifferences] = useState({});
  const [addMinuteLoading, setAddMinuteLoading] = useState(false);
  const [editingAIMessage, setEditingAIMessage] = useState(false);
  const [editingAIMessageContents, setEditingAIMessageContents] = useState('');
  const [editingAIMessageID, setEditingAIMessageID] = useState('');

  // State for users (assumes you have a 'users' table with id, initials, and color)
  const [users, setUsers] = useState([]);
  // State for active viewers
  const [activeViewers, setActiveViewers] = useState([]);

  const messagesEndRef = useRef(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, conversationID, AIMessages]);

  // Fetch users from your 'users' table (adjust query as needed)
  useEffect(() => {
    const fetchUsers = async () => {
      const { data, error } = await supabase.from('users').select('*');
      if (error) {
        console.error('Error fetching users:', error.message);
      } else {
        setUsers(data);
      }
    };
    fetchUsers();
  }, []);

  // Helper function to get user info by user id
  function getUserInfoById(userId) {
    return users.find(user => user.id === userId) || { initials: '??', color: '#ccc' };
  }

  // Fetch conversation messages and subscribe for real-time updates
  useEffect(() => {
    if (!conversationID) return;

    // Fetch existing messages
    const getMessages = async () => {
      try {
        const { data, error } = await supabase
          .from('conversation_messages')
          .select('*')
          .eq('conversation_id', conversationID)
          .order('date', { ascending: true });

        if (error) {
          console.error('Error fetching conversation messages:', error.message);
          return;
        }
        
        setMessages(processMessages(data));
      } catch (error) {
        console.error('Error fetching conversation messages:', error.message);
      }
    };

    getMessages();

    // Real-time subscription for conversation messages
    const channel = supabase
      .channel(`conversation_messages:${conversationID}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'conversation_messages', filter: `conversation_id=eq.${conversationID}` },
        (payload) => {
          console.log('Realtime event:', payload);

          setMessages((prevMessages) => {
            if (payload.eventType === 'INSERT') {
              return [...prevMessages, payload.new];
            } else if (payload.eventType === 'UPDATE') {
              return prevMessages.map(msg =>
                msg.id === payload.new.id ? payload.new : msg
              );
            } else if (payload.eventType === 'DELETE') {
              return prevMessages.filter(msg => msg.id !== payload.old.id);
            }
            return prevMessages;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversationID]);

  // Fetch AI messages and subscribe for real-time updates
  useEffect(() => {
    const getAIMessages = async () => {
      try {
        const { data, error } = await supabase
          .from('scheduled_messages')
          .select('*')
          .eq('conversation_id', conversationID);

        const convertedData = data.map(message => ({
          ...message,
          send_at: new Date(message.send_at).toLocaleString()
        }));

        setAIMessages(convertedData);
      } catch (error) {
        console.error('Error getting AI messages:', error.message);
      }
    };
    getAIMessages();

    const channel = supabase
      .channel(`scheduled_messages:${conversationID}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'scheduled_messages', filter: `conversation_id=eq.${conversationID}` },
        (payload) => {
          console.log('Realtime event:', payload);

          setAIMessages((prevMessages) => {
            const newMessage = {
              ...payload.new,
              send_at: new Date(payload.new.send_at).toLocaleString()
            };

            if (payload.eventType === 'INSERT') {
              return [...prevMessages, newMessage];
            } else if (payload.eventType === 'UPDATE') {
              return prevMessages.map(msg =>
                msg.id === newMessage.id ? newMessage : msg
              );
            } else if (payload.eventType === 'DELETE') {
              return prevMessages.filter(msg => msg.id !== payload.old.id);
            }
            return prevMessages;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversationID]);

  // Process messages to attach reactions
  const processMessages = (messages) => {
    const processedMessages = [];

    messages.forEach((msg) => {
      const reactionMatch = msg.body.match(/^(.+?) reacted (❤️|👍|😂|😢|😡) to (?:your|(.+?)['’]s) message "(.*?)"$/);

      if (reactionMatch) {
        const [, user, reaction, , originalText] = reactionMatch;
        const originalMsg = processedMessages.find(m => m.body.trim() === originalText.trim());
        if (originalMsg) {
          originalMsg.reactions = originalMsg.reactions || [];
          originalMsg.reactions.push({ user, reaction });
        }
      } else {
        processedMessages.push({ ...msg, reactions: [] });
      }
    });

    return processedMessages;
  };

  // Send a new message
  async function sendMessage() {
    try {
      const data = {
        conversation_id: conversationID,
        message: newMessageText,
        communication_type: 'channel'
      };
      await axios.post(`${serverURL}/sendMessage`, data);
      setNewMessageText('');
    } catch (error) {
      console.log("error sending message", error);
    }
  }

  // Calculate time differences for AI messages
  useEffect(() => {
    if (AIMessages.length === 0) return;

    const calculateTimeDifferences = () => {
      const updatedDifferences = {};
      AIMessages.forEach((message) => {
        const now = new Date().getTime();
        const start = new Date().getTime();
        const end = message.send_at ? new Date(message.send_at).getTime() : now;

        let diff = Math.max(0, end - start);
        const minutes = Math.floor(diff / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);

        updatedDifferences[message.id] = { minutes, seconds };
      });

      setTimeDifferences(updatedDifferences);
    };

    calculateTimeDifferences();
    const interval = setInterval(calculateTimeDifferences, 1000);
    return () => clearInterval(interval);
  }, [AIMessages]);

  // Add one minute to an AI message's scheduled time
  async function addOneMin(previousTime) {
    try {
      setAddMinuteLoading(true);
      const data = {
        conversationID: conversationID,
        previousTime: previousTime
      };
      await axios.post(`http://hostaway.stevenbrekalo.com/addOneMinToAiMessage`, data);
      setAddMinuteLoading(false);
    } catch (error) {
      console.log("error sending message", error);
      setAddMinuteLoading(false);
    }
  }

  // Update an AI message
  async function updateAIMessage() {
    try {
      const { data, error } = await supabase
        .from('scheduled_messages')
        .update({ body: editingAIMessageContents })
        .eq('id', editingAIMessageID);
      if (error) throw error;
      setEditingAIMessage(false);
      console.log('Message updated successfully:', data);
    } catch (error) {
      console.error('Error updating message:', error.message);
    }
  }

  // Delete an AI message
  async function deleteAIMessage(messageID) {
    try {
      const { data, error } = await supabase
        .from('scheduled_messages')
        .delete()
        .eq('id', messageID);
      if (error) throw error;
      console.log('Message deleted successfully:', data);
    } catch (error) {
      console.error('Error deleting message:', error.message);
    }
  }

  // Send an AI message
  async function sendAIMessage() {
    try {
      const data = {
        conversation_id: conversationID,
        message: AIMessages[0].body,
        communication_type: 'channel'
      };
      await axios.post(`https://hostaway.stevenbrekalo.com/sendMessage`, data);
      deleteAIMessage(AIMessages[0].id);
      setNewMessageText('');
    } catch (error) {
      console.log("error sending message", error);
    }
  }




  // For real-time viewers: add current viewer on mount and remove on unmount
  useEffect(() => {
    let userId;
  
    async function setupViewer() {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) {
        console.error("Error getting user:", error);
        return;
      }
      if (!user || !conversationID) return;
      userId = user.id;
  
      try {
        // Step 1: Remove user from any previous conversation
        await supabase.from('active_viewers').delete().eq('user_id', userId);
  
        // Step 2: Add user to the new conversation
        await supabase.from('active_viewers').upsert([
          { conversation_id: conversationID, user_id: userId, last_seen: new Date(), is_typing: false }
        ]);
  
        console.log(`User ${userId} is now active in conversation ${conversationID}`);
  
      } catch (err) {
        console.error("Error setting up active viewer:", err);
      }
  
      // Update last_seen every 30 seconds
      const intervalId = setInterval(async () => {
        await supabase.from('active_viewers').update({ last_seen: new Date() }).eq('user_id', userId);
      }, 30000);
  
      // Cleanup function
      const handleBeforeUnload = () => {
        if (!userId) return;
        navigator.sendBeacon(`${serverURL}/removeViewer`, JSON.stringify({ userId }));
      };
  
      window.addEventListener('beforeunload', handleBeforeUnload);
  
      return () => {
        clearInterval(intervalId);
        window.removeEventListener('beforeunload', handleBeforeUnload);
  
        // Remove from active_viewers when switching conversations
        supabase.from('active_viewers').delete().eq('user_id', userId)
          .then(({ error }) => {
            if (error) console.error("Error removing viewer:", error);
          });
      };
    }
  
    const cleanupPromise = setupViewer();
  
    return () => {
      if (cleanupPromise && typeof cleanupPromise.then === 'function') {
        // Cleanup already handled inside setupViewer
      }
    };
  }, [conversationID]);
  
  
  




  // Real-time subscription to update active viewers state
  useEffect(() => {
    const fetchActiveViewers = async () => {
      const { data, error } = await supabase
        .from('active_viewers')
        .select('*')
        .eq('conversation_id', conversationID);
      if (error) {
        console.error('Error fetching active viewers:', error.message);
      } else {
        setActiveViewers(data);
      }
    };

    fetchActiveViewers();

    const channel = supabase
      .channel(`active_viewers:${conversationID}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'active_viewers', filter: `conversation_id=eq.${conversationID}` },
        (payload) => {
        //   console.log('Active viewer update:', payload);
          // For simplicity, re-fetch the list on any change
          fetchActiveViewers();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversationID]);




  const textareaRef = useRef(null);
  const avatarRowRef = useRef(null);

  useEffect(() => {
      const textarea = textareaRef.current;
      const avatarRow = avatarRowRef.current;
      if (textarea) {
          textarea.style.height = '35px'; // Reset height
          textarea.style.height = Math.min(textarea.scrollHeight, 100) + 'px'; // Adjust height
          avatarRow.style.marginBottom = textarea.scrollHeight - 35 + 'px'; // Adjust margin
      }
  }, [newMessageText]);






    // At the top of your component
    const typingTimeoutRef = useRef(null);

    // Function to update typing status in the database
    async function updateTypingStatus(status) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    await supabase
        .from('active_viewers')
        .update({ is_typing: status, last_seen: new Date() })
        .eq('user_id', user.id);
    }

    // Handler for text input changes
    function handleTyping(e) {
    setNewMessageText(e.target.value);

    // Set typing status to true
    updateTypingStatus(true);

    // Clear previous timeout if exists
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

    // Set a timeout to mark as not typing after 1.5 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
        updateTypingStatus(false);
    }, 1500);
    }




  return (
    <div className='MessagesMainContainer'>
        <div className='MessagesContents'>
            {messages.map((x, index) => (
            x.status === 'sent' ? (
                <div key={index} className='MessageRow' style={{ justifyContent: x.is_incoming ? 'flex-start' : 'flex-end', paddingTop: x.reactions?.length > 0 && 15 }}>
                <div className={x.is_incoming ? 'MessageContainerReceived' : 'MessageContainerSent'}>
                    <div>{x.body}</div>
                    {x.reactions?.length > 0 && (
                    <div className="MessageReactionContainer">
                        {x.reactions.map((r, i) => (
                        <span key={i} className="MessageReaction">{r.reaction}</span>
                        ))}
                    </div>
                    )}
                </div>
                </div>
            ) : (
                <div key={index} className='MessageRow' style={{ justifyContent: 'flex-end' }}>
                <div className='MessageContainerScheduled'>
                    <div>{x.body}</div>
                </div>
                </div>
            )
            ))}

            {AIMessages.map((x, index) => (
            <div className='MessageRowAIWrapper' key={index}>
                <div className='MessageRowAIOptionsFloatingRow'>
                <div className='MessageRowAIOptionsFloatingRowButton' onClick={() => { deleteAIMessage(x.id) }}>
                    <DeleteIcon width={24} height={24} color='#666' />
                </div>
                <div className='MessageRowAIOptionsFloatingRowButton' onClick={() => { addOneMin(x.send_at) }}>
                    {addMinuteLoading ? <CircularProgress style={{ width: 20, height: 20 }} /> : <ClockIcon width={24} height={24} color='#666' />}
                </div>
                <div className='MessageRowAIOptionsFloatingRowButton' onClick={() => { setEditingAIMessage(true); setEditingAIMessageContents(x.body); setEditingAIMessageID(x.id) }}>
                    <EditIcon width={24} height={24} color='#666' />
                </div>
                <div className='MessageRowAIOptionsFloatingRowButton' onClick={() => { sendAIMessage() }}>
                    <SendMessageIcon width={24} height={24} color='#666' />
                </div>
                </div>
                <div className='MessageRow' style={{ justifyContent: 'flex-end' }}>
                <div className='MessageContainerScheduled'>
                    <div>{x.body}</div>
                    {timeDifferences[x.id] && (
                    <div style={{ fontSize: '12px', marginTop: '5px' }}>
                        Scheduled in {timeDifferences[x.id].minutes} min {timeDifferences[x.id].seconds} sec
                    </div>
                    )}
                </div>
                </div>
            </div>
            ))}

            <div ref={messagesEndRef} />
        </div>

            


        <div className='MessagesInputContainer'>
       
            {/* Active Viewers indicator */}
            <div ref={avatarRowRef} className="MessagesActiveViewersRow">
            {activeViewers
                .map((viewer) => ({
                    ...viewer,
                    userInfo: getUserInfoById(viewer.user_id),
                }))
                .sort((a, b) => a.userInfo.initials.localeCompare(b.userInfo.initials))
                .map(({ user_id, userInfo, is_typing }) => (
                    <div key={user_id} className="MessagesActiveViewersAvatar" style={{ backgroundColor: '#' + userInfo.color + 'ff', width: is_typing ? 50 : 15, marginRight: is_typing ? 5 : -10 }}>
                        {userInfo.initials}
                        {is_typing && 
                            <div className="MessagesActiveViewersTypingIndicatorContainer">
                                <div class="MessagesActiveViewersTypingIndicatorDot"></div>
                                <div class="MessagesActiveViewersTypingIndicatorDot"></div>
                                <div class="MessagesActiveViewersTypingIndicatorDot"></div>
                            </div>
                        }
                    </div>
                ))}
            </div>


            {editingAIMessage ? (
                <div className='MessagesInputRow'>
                <textarea type='text' className='MessagesInput' placeholder='Type a message...' onChange={(e) => { setEditingAIMessageContents(e.target.value) }} value={editingAIMessageContents} />
                <div className='MessagesSendButton' onClick={() => { updateAIMessage() }} style={{ backgroundColor: newMessageText.length > 0 ? '#222' : '#ccc' }}>
                    <SaveRounded width={24} height={24} color='#fff' />
                </div>
                </div>
            ) : (
                <div className='MessagesInputRow'>
                <textarea ref={textareaRef} type='text' className='MessagesInput' placeholder='Type a message...' onChange={handleTyping} value={newMessageText} />
                <div className='MessagesSendButton' onClick={() => { sendMessage() }} style={{ backgroundColor: newMessageText.length > 0 ? '#222' : '#ccc' }}>
                    <SendMessageIcon width={24} height={24} color='#fff' />
                </div>
                </div>
            )}
        </div>

    </div>
  );
}

export default Messages;
